import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';

const tableStyles = {
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '20px', fontSize: '0.9rem' },
  // ⬇️ 텍스트 가운데 정렬
  th: { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4', textAlign: 'center' },
  td: { border: '1px solid #ddd', padding: '8px', textAlign: 'center' },
  select: { padding: '5px' },
  button: { padding: '5px 10px', marginLeft: '5px', cursor: 'pointer' }
};

const ROLE_LABEL = {
  pending: '승인대기',
  user: '일반사용자',
  admin: '관리자'
};

/** 관리자 사용자 목록 API 쿼리 (등급·상태 전체는 파라미터 생략) */
function buildAdminUserParams(role, status, name) {
  const params = {};
  if (role && role !== 'all') params.role = role;
  if (status && status !== 'all') params.status = status;
  const trimmed = (name || '').trim();
  if (trimmed) params.name = trimmed;
  return params;
}

const ADMIN_TABS = [
  { id: 'registration', label: '가입 코드 관리' },
  { id: 'notice', label: '공지사항 설정' },
  { id: 'users', label: '사용자 관리' },
];

const AdminSettingsPage = () => {
  const location = useLocation();
  const userListAutoSkipRef = useRef(true);
  const [activeTab, setActiveTab] = useState('registration');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchRole, setUserSearchRole] = useState('all');
  const [userSearchStatus, setUserSearchStatus] = useState('active');
  const [userSearchName, setUserSearchName] = useState('');
  const [debouncedSearchName, setDebouncedSearchName] = useState('');
  const [error, setError] = useState('');
  const [registrationCode, setRegistrationCode] = useState('');
  const [noticeEnabled, setNoticeEnabled] = useState(false);
  const [noticeTitle, setNoticeTitle] = useState('');
  const [noticeContent, setNoticeContent] = useState('');
  const [noticeAttachments, setNoticeAttachments] = useState([]);
  const [noticeSaveLoading, setNoticeSaveLoading] = useState(false);
  const [noticeUploadLoading, setNoticeUploadLoading] = useState(false);

  const runUserListFetch = useCallback(async (role, status, nameQuery) => {
    setUsersLoading(true);
    try {
      const params = buildAdminUserParams(role, status, nameQuery);
      const { data } = await api.get('/admin/users', { params });
      setUsers(data);
    } catch (err) {
      alert('사용자 목록 조회 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // 성명 입력은 디바운스 후 자동 검색에 반영
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchName(userSearchName), 350);
    return () => clearTimeout(t);
  }, [userSearchName]);

  // 등급·상태·(디바운스된) 성명 변경 시 자동 검색 (초기 마운트 1회는 제외 — 아래 초기 로딩과 중복 방지)
  useEffect(() => {
    if (userListAutoSkipRef.current) {
      userListAutoSkipRef.current = false;
      return;
    }
    runUserListFetch(userSearchRole, userSearchStatus, debouncedSearchName);
  }, [userSearchRole, userSearchStatus, debouncedSearchName, runUserListFetch]);

  // 1. 페이지 로드 시 데이터 가져오기 (사용자 목록: 등급 전체, 상태 사용중 기본)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const initialUserParams = buildAdminUserParams('all', 'active', '');
        const [usersRes, codeRes, noticeRes] = await Promise.all([
          api.get('/admin/users', { params: initialUserParams }),
          api.get('/admin/config/registration-code'),
          api.get('/admin/config/dashboard-notice').catch(() => ({ data: { enabled: false, title: '', content: '', attachments: [] } }))
        ]);
        setUsers(usersRes.data);
        setRegistrationCode(codeRes.data.value);
        setNoticeEnabled(!!noticeRes.data.enabled);
        setNoticeTitle(noticeRes.data.title || '');
        setNoticeContent(noticeRes.data.content || '');
        setNoticeAttachments(Array.isArray(noticeRes.data.attachments) ? noticeRes.data.attachments : []);
      } catch (err) {
        setError(err.response?.data?.message || '데이터 로딩 실패');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // 대시보드 등에서 /admin-settings#notice 로 진입 시 공지 탭 표시
  useEffect(() => {
    if (location.hash === '#notice') {
      setActiveTab('notice');
    }
  }, [location.hash]);

  // 2. 등급 변경 핸들러
  const handleRoleChange = async (row, newRole) => {
    const label = ROLE_LABEL[newRole] || newRole;
    if (
      !window.confirm(
        `'${row.name}'(${row.userId})님의 등급을 「${label}」(으)로 변경하시겠습니까?`
      )
    ) {
      return;
    }
    try {
      await api.put(`/admin/users/${row.id}`, { role: newRole });
      await runUserListFetch(userSearchRole, userSearchStatus, userSearchName);
      alert('등급이 변경되었습니다.');
    } catch (err) {
      alert('등급 변경 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleStatusChange = async (userId, currentStatus) => {
    const nextStatus = currentStatus === 'active' ? 'disabled' : 'active';
    const confirmMessage = nextStatus === 'disabled'
      ? '해당 사용자를 정지하시겠습니까? 로그인할 수 없게 됩니다.'
      : '해당 사용자의 정지를 해제하시겠습니까?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await api.put(`/admin/users/${userId}`, { status: nextStatus });
      await runUserListFetch(userSearchRole, userSearchStatus, userSearchName);
      alert(nextStatus === 'disabled' ? '사용자가 정지되었습니다.' : '사용자 정지가 해제되었습니다.');
    } catch (err) {
      alert('상태 변경 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  // 3. 비밀번호 초기화 핸들러
  const handlePasswordReset = async (userId, name) => {
    const newPassword = prompt(`'${name}' 님의 새 비밀번호를 입력하세요:`);
    if (!newPassword) {
      alert('취소되었습니다.');
      return;
    }
    
    try {
      await api.put(`/admin/users/${userId}`, { password: newPassword });
      alert('비밀번호가 성공적으로 변경(초기화)되었습니다.');
    } catch (err) {
      alert('비밀번호 변경 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  // 4. 가입 코드 변경 핸들러
  const handleCodeChange = async (e) => {
    e.preventDefault();
    if (!window.confirm(`가입 코드를 '${registrationCode}'(으)로 변경하시겠습니까?`)) {
      return;
    }
    try {
      await api.put('/admin/config/registration-code', { value: registrationCode });
      alert('가입 코드가 변경되었습니다.');
    } catch (err) {
      alert('가입 코드 변경 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  // 5. 공지 설정 저장
  const handleNoticeSave = async (e) => {
    e.preventDefault();
    setNoticeSaveLoading(true);
    try {
      await api.put('/admin/config/dashboard-notice', {
        enabled: noticeEnabled,
        title: noticeTitle,
        content: noticeContent,
        attachments: noticeAttachments
      });
      alert('공지 설정이 저장되었습니다.');
    } catch (err) {
      alert('공지 저장 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setNoticeSaveLoading(false);
    }
  };

  // 6. 공지 첨부파일 업로드
  const handleNoticeFileAdd = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    const formData = new FormData();
    formData.append('file', file);
    setNoticeUploadLoading(true);
    try {
      const res = await api.post('/admin/config/dashboard-notice/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNoticeAttachments((prev) => [...prev, res.data]);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message;
      if (status === 413 || (msg && typeof msg === 'string')) {
        alert(msg || '첨부파일 용량을 초과했습니다. 파일당 최대 5MB까지 첨부 가능합니다.');
      } else {
        alert('파일 업로드에 실패했습니다. 파일당 최대 5MB, PDF·JPG·PNG·GIF만 첨부 가능합니다.');
      }
    } finally {
      setNoticeUploadLoading(false);
      e.target.value = '';
    }
  };

  // 7. 공지 첨부파일 제거
  const handleNoticeFileRemove = async (filename) => {
    if (!window.confirm('이 첨부파일을 제거하시겠습니까?')) return;
    try {
      await api.delete(`/admin/config/dashboard-notice/attachment/${encodeURIComponent(filename)}`);
      setNoticeAttachments((prev) => prev.filter((a) => a.filename !== filename));
    } catch (err) {
      alert('첨부파일 삭제 실패: ' + (err.response?.data?.message || err.message));
    }
  };

  if (loading) return <p>로딩 중...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  const sectionCardStyle = {
    border: '1px solid #cbd5e1',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '28px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    background: '#ffffff'
  };
  const sectionHeaderStyle = {
    padding: '16px 24px',
    background: '#f1f5f9',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#334155'
  };
  const sectionBodyStyle = {
    padding: '24px'
  };

  const tabBarStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    borderBottom: '2px solid #e2e8f0',
    marginBottom: '24px',
    marginTop: '8px',
  };

  const tabButtonStyle = (isActive) => ({
    padding: '12px 18px',
    border: 'none',
    borderBottom: isActive ? '3px solid #2563eb' : '3px solid transparent',
    marginBottom: '-2px',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: isActive ? 600 : 500,
    color: isActive ? '#1e293b' : '#64748b',
  });

  return (
    <div>
      <h2 style={{ marginBottom: '4px' }}>관리자 설정</h2>
      <div role="tablist" aria-label="관리자 설정 메뉴" style={tabBarStyle}>
        {ADMIN_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`admin-tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`admin-panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => setActiveTab(tab.id)}
            style={tabButtonStyle(activeTab === tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'registration' && (
      <div id="admin-panel-registration" role="tabpanel" aria-labelledby="admin-tab-registration" style={sectionCardStyle}>
        <div style={sectionHeaderStyle}>가입 코드 관리</div>
        <div style={sectionBodyStyle}>
        <p style={{ margin: '0 0 16px 0', fontSize: '0.9rem', color: '#64748b', lineHeight: 1.6 }}>
          신규 사용자가 회원가입 시 입력하는 코드입니다. 올바른 코드를 알고 있는 사람만 계정을 생성할 수 있어, 불필요한 가입을 막을 수 있습니다.
        </p>
        <form onSubmit={handleCodeChange}>
          <input
            type="text"
            value={registrationCode}
            onChange={(e) => setRegistrationCode(e.target.value)}
            style={{ ...tableStyles.select, width: '200px' }}
          />
          <button type="submit" style={tableStyles.button}>코드 변경</button>
        </form>
        </div>
      </div>
      )}

      {activeTab === 'notice' && (
      <div id="admin-panel-notice" role="tabpanel" aria-labelledby="admin-tab-notice" style={sectionCardStyle}>
        <div style={sectionHeaderStyle}>공지사항 설정</div>
        <div style={sectionBodyStyle}>
        <form onSubmit={handleNoticeSave} style={{ maxWidth: '600px' }}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={noticeEnabled}
                onChange={(e) => setNoticeEnabled(e.target.checked)}
              />
              <span>대시보드 공지 사용</span>
            </label>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>제목</label>
            <input
              type="text"
              value={noticeTitle}
              onChange={(e) => setNoticeTitle(e.target.value)}
              placeholder="공지 제목"
              style={{ ...tableStyles.select, width: '100%', maxWidth: '400px' }}
            />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>본문 (순수 텍스트)</label>
            <textarea
              value={noticeContent}
              onChange={(e) => setNoticeContent(e.target.value)}
              placeholder="공지 내용을 입력하세요"
              rows={6}
              style={{ ...tableStyles.select, width: '100%', maxWidth: '500px', resize: 'vertical' }}
            />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>첨부파일 (PDF, 이미지)</label>
            <span style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', marginBottom: '6px' }}>
              파일당 최대 5MB (PDF, JPG, PNG, GIF만 가능)
            </span>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif"
              onChange={handleNoticeFileAdd}
              disabled={noticeUploadLoading}
              style={{ display: 'block', marginBottom: '8px' }}
            />
            {noticeAttachments.length > 0 && (
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                {noticeAttachments.map((a) => (
                  <li key={a.filename} style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{a.originalname || a.filename}</span>
                    <button
                      type="button"
                      onClick={() => handleNoticeFileRemove(a.filename)}
                      style={{ ...tableStyles.button, padding: '2px 8px', fontSize: '0.85rem' }}
                    >
                      삭제
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button type="submit" style={tableStyles.button} disabled={noticeSaveLoading}>
            {noticeSaveLoading ? '저장 중...' : '공지 저장'}
          </button>
        </form>
        </div>
      </div>
      )}

      {activeTab === 'users' && (
      <div id="admin-panel-users" role="tabpanel" aria-labelledby="admin-tab-users" style={sectionCardStyle}>
        <div style={sectionHeaderStyle}>사용자 관리</div>
        <div style={sectionBodyStyle}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          runUserListFetch(userSearchRole, userSearchStatus, userSearchName);
        }}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: '12px 16px',
          marginBottom: '16px',
          padding: '12px',
          background: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}
      >
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px', color: '#475569' }}>등급</label>
          <select
            style={{ ...tableStyles.select, minWidth: '140px' }}
            value={userSearchRole}
            onChange={(e) => setUserSearchRole(e.target.value)}
          >
            <option value="all">전체</option>
            <option value="pending">승인대기</option>
            <option value="user">일반사용자</option>
            <option value="admin">관리자</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px', color: '#475569' }}>상태</label>
          <select
            style={{ ...tableStyles.select, minWidth: '120px' }}
            value={userSearchStatus}
            onChange={(e) => setUserSearchStatus(e.target.value)}
          >
            <option value="all">전체</option>
            <option value="active">사용중</option>
            <option value="disabled">정지됨</option>
          </select>
        </div>
        <div style={{ flex: '1 1 160px', minWidth: '140px' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px', color: '#475569' }}>성명</label>
          <input
            type="text"
            value={userSearchName}
            onChange={(e) => setUserSearchName(e.target.value)}
            placeholder="성명 일부 입력"
            style={{ ...tableStyles.select, width: '100%', maxWidth: '220px' }}
          />
        </div>
        <button type="submit" style={{ ...tableStyles.button, marginLeft: 0 }} disabled={usersLoading}>
          {usersLoading ? '검색 중...' : '검색'}
        </button>
      </form>
      {usersLoading && (
        <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#64748b' }}>목록을 불러오는 중...</p>
      )}
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <th style={tableStyles.th}>아이디</th>
            <th style={tableStyles.th}>성명</th>
            <th style={tableStyles.th}>등급</th>
            <th style={tableStyles.th}>상태</th>
            <th style={tableStyles.th}>가입일</th>
            <th style={tableStyles.th}>마지막 활동</th>
            <th style={tableStyles.th}>관리</th>
          </tr>
        </thead>
        <tbody>
          {!usersLoading && users.length === 0 && (
            <tr>
              <td colSpan={7} style={{ ...tableStyles.td, padding: '24px', color: '#64748b' }}>
                조건에 맞는 사용자가 없습니다.
              </td>
            </tr>
          )}
          {users.map(user => (
            <tr key={user.id}>
              <td style={tableStyles.td}>{user.userId}</td>
              <td style={{...tableStyles.td, textAlign: 'left'}}>{user.name}</td>
              <td style={tableStyles.td}>
                <select
                  style={tableStyles.select}
                  value={user.role}
                  onChange={(e) => handleRoleChange(user, e.target.value)}
                >
                  <option value="pending">승인대기</option>
                  <option value="user">일반사용자</option>
                  <option value="admin">관리자</option>
                </select>
              </td>
              <td style={tableStyles.td}>
                <button
                  type="button"
                  style={{
                    ...tableStyles.button,
                    backgroundColor: user.status === 'active' ? '#28a745' : '#dc3545',
                    color: '#fff'
                  }}
                  onClick={() => handleStatusChange(user.id, user.status)}
                >
                  {user.status === 'active' ? '사용중' : '정지됨'}
                </button>
              </td>
              <td style={tableStyles.td}>{new Date(user.createdAt).toLocaleString()}</td>
              <td style={tableStyles.td}>{user.lastActivity ? new Date(user.lastActivity).toLocaleString() : '-'}</td>
              <td style={tableStyles.td}>
                <button 
                  style={tableStyles.button}
                  onClick={() => handlePasswordReset(user.id, user.name)}
                >
                  비밀번호 변경
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminSettingsPage;

